import React, { useRef, useState } from "react";

import Button from "@cayuse/shared-react.components.button";
import IconButton from "@cayuse/shared-react.components.icon-button";
import { AlertDialog } from "@cayuse/shared-react.components.modal-dialog";
import PropTypes from "prop-types";


import { useRuleBuilderContext } from "containers/Rules/context/useRuleBuilderContext";
import { NEW_RULE_ID } from "globals/constants";
import { useDictionary } from "hooks";


import RuleGroup from "./RuleGroup/RuleGroup";
import styles from "./styles.css";

const ConditionBuilder = ({
	rule,
	isNew,
	handleRuleCancel,
	handleRuleSave,
}) => {
	const {
		addGroup,
		isEditingOperand,
		setEditingAction,
		formChanged,
		setFormChanged,
		setEditingCondition,
		editingCondition,
		isEditingAction,
	} = useRuleBuilderContext();
	const [ isModalOpen, setModalOpen ] = useState(false);
	const tooltipValueRef = useRef(null);
	const dictionary = useDictionary("ruleBuilder");
	const multiGroup = rule.condition?.operands?.length > 1;

	const editCondition = () => {
		if (rule.id === NEW_RULE_ID) {
			rule.targetId = null;
			rule.ruleType = null;
		}

		rule.isConditionEditing = true;
		rule.isActionEditing = false;
		setEditingCondition(true);
		setEditingAction(false);
	};

	const handleEditCondition = () => {
		if (formChanged || !isNew) {
			setModalOpen(true);
		} else {
			editCondition();
		}
	};

	const handleModalConfirm = () => {
		setModalOpen(false);
		setFormChanged(false);
		editCondition();
	};

	return (
		<div
			className={styles.conditionContainer}
			data-tip={tooltipValueRef.current}
		>
			<div className={styles.conditionWrapper}>
				<div
					className={`${styles.conditionRow} ${
                        multiGroup ? styles.multiGroup : ""
                    }`}
				>
					{rule.condition ? (
						<>
							<div className={styles.conditionOperand}>
								{(rule.condition.operands ?? []).map(
									(group, groupIndex) => (
										<RuleGroup
											group={group}
											index={groupIndex}
											multiGroup={multiGroup}
											isEditing={rule.isConditionEditing}
										/>
									)
								)}
							</div>
							{!rule.isConditionEditing && (
								<IconButton
									secondary
									labelPosition="left"
									icon="pencil"
									onClick={handleEditCondition}
									className={styles.editConditionButton}
									disabled={
										editingCondition ||
                                        (isEditingAction &&
                                            !rule.isActionEditing)
									}
								>
									{dictionary.conditionBuilder
										.editCondition || "Edit Condition"}
								</IconButton>
							)}
						</>
					) : (
                        dictionary.conditionBuilder?.missingCondition ||
                        "Unable to display the rule condition"
					)}
				</div>
				{rule.isConditionEditing && (
					<div className={styles.addGroupButton}>
						<IconButton
							secondary
							labelPosition="left"
							icon="plus"
							onClick={() => addGroup({ rule })}
							disabled={isEditingOperand}
						>
							{dictionary.conditionBuilder.addGroup ||
                                "Add Group"}
						</IconButton>
					</div>
				)}
			</div>
			{rule.isConditionEditing && (
				<div className={styles.saveButtonGroup}>
					<Button type="button" secondary onClick={handleRuleCancel}>
						{dictionary.cancelNewRule || "Cancel"}
					</Button>
					<Button
						type="button"
						disabled={isEditingOperand}
						onClick={handleRuleSave}
					>
						{dictionary.saveNewRule || "Save"}
					</Button>
				</div>
			)}
			{isModalOpen && (
				<AlertDialog
					title={
						dictionary.conditionBuilder.editAlertDialogTitle ??
                        "Edit Condition?"
					}
					description={
						dictionary.conditionBuilder.editAlertDialogMessage ||
                        "Editing the Condition will require you to re-select an action"
					}
					isOpen={isModalOpen}
					onConfirm={handleModalConfirm}
					confirmText={
						dictionary.conditionBuilder.editAlertDialogConfirm ??
                        "Edit Condition"
					}
					onCancel={() => setModalOpen(false)}
				/>
			)}
		</div>
	);
};

ConditionBuilder.propTypes = {
	formType: PropTypes.string,
	isNew: PropTypes.bool,
	rule: PropTypes.shape({
		type: PropTypes.string,
	}).isRequired,
};

export default ConditionBuilder;
